
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { DraftCampaign } from "@/hooks/campaign/hooks/useCampaignInit";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Loader2, FileEdit, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { useLocalDraftList } from '@/hooks/useLocalDraftList';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AllDraftsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  drafts: DraftCampaign[];
  loading: boolean;
  onDraftDeleted?: () => void;
}

export const AllDraftsDialog = ({ 
  open, 
  onOpenChange,
  drafts,
  loading,
  onDraftDeleted
}: AllDraftsDialogProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const { deleteDraft } = useLocalDraftList();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);


  const handleConfirmDelete = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(id);
  };

  const handleDeleteDraft = async () => {
    if (!confirmDeleteId) return;
    
    const id = confirmDeleteId;
    setDeletingId(id);
    
    try {
      await deleteDraft(id);
      
      toast({
        title: t('draftDeleted'),
        description: t('draftDeletedSuccess'),
      });
      
      // Trigger refresh
      if (onDraftDeleted) {
        onDraftDeleted();
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast({
        title: t('errorDeletingDraft'),
        description: t('problemDeletingDraft'),
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('savedDrafts')}</DialogTitle>
            <DialogDescription>{t('continuePreviousDraft')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">{t('loadingDrafts')}</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('savedDrafts')}</DialogTitle>
            <DialogDescription>{t('continuePreviousDraft')}</DialogDescription>
          </DialogHeader>
          
          {drafts.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <p>{t('noDrafts')}</p>
            </div>
          ) : (
            <div className="grid gap-3 py-4">
              {drafts.map((draft) => (
                <div 
                  key={draft.id}
                  className="p-4 rounded-lg border border-border hover:border-primary/30 bg-background/80 hover:bg-background transition-colors"
                >
                  <Link
                    to={`/campaign/draft/${draft.id}`}
                    className="flex justify-between items-center"
                    onClick={() => onOpenChange(false)}
                  >
                    <div>
                      <p className="font-medium text-foreground">{draft.campaign_name || 'Untitled Campaign'}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('lastEdited')}: {format(new Date(draft.updated_at), 'PPp')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileEdit className="h-4 w-4 text-muted-foreground" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive"
                        onClick={(e) => handleConfirmDelete(draft.id, e)}
                        disabled={deletingId === draft.id}
                      >
                        {deletingId === draft.id ? 
                          <div className="h-4 w-4 rounded-full border-2 border-b-transparent animate-spin"></div> : 
                          <Trash2 className="h-4 w-4" />
                        }
                      </Button>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={() => setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmDeleteDraft')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirmDeleteDraft')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteDraft}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
